import type { AltYBootstrapApiConfig, AltYBootstrapAuthConfig, AltYTauriInvoke } from '../types/global';

type NativeAuthBridge = {
  getAuthToken?: () => string | null | Promise<string | null>;
  setAuthToken?: (token: string) => void | Promise<void>;
  clearAuthToken?: () => void | Promise<void>;
};

type NativeAuthOptions = {
  api?: Partial<AltYBootstrapApiConfig>;
  auth?: Partial<AltYBootstrapAuthConfig>;
};

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

function cleanUrl(value?: string | null): string | undefined {
  const trimmed = typeof value === 'string' ? trimTrailingSlashes(value.trim()) : '';
  return trimmed || undefined;
}

function absoluteUrl(value?: string | URL | null): string {
  if (!value) return '';
  try {
    return trimTrailingSlashes(new URL(String(value), window.location.href).href);
  } catch {
    return '';
  }
}

function inputUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string' || input instanceof URL) return absoluteUrl(input);
  if (typeof Request !== 'undefined' && input instanceof Request) return absoluteUrl(input.url);
  return '';
}

function inputHeaders(input: RequestInfo | URL): HeadersInit | undefined {
  return typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
}

function inputCredentials(input: RequestInfo | URL): RequestCredentials | undefined {
  return typeof Request !== 'undefined' && input instanceof Request ? input.credentials : undefined;
}

function urlEquals(url: string, configuredUrl?: string | null): boolean {
  const target = absoluteUrl(configuredUrl);
  return !!target && url === target;
}

function urlMatches(url: string, configuredBase?: string | null): boolean {
  const base = absoluteUrl(configuredBase);
  return !!base && (url === base || url.startsWith(`${base}/`));
}

function configuredApi(options: NativeAuthOptions = {}): Partial<AltYBootstrapApiConfig> {
  return options.api
    || window.altYResolvedBootstrap?.api
    || window.altYBootstrapConfig?.api
    || window.altYStandaloneConfig?.api
    || {};
}

function configuredAuth(options: NativeAuthOptions = {}): Partial<AltYBootstrapAuthConfig> {
  return options.auth
    || window.altYResolvedBootstrap?.auth
    || window.altYBootstrapConfig?.auth
    || window.altYStandaloneConfig?.auth
    || {};
}

function authUrls(options: NativeAuthOptions = {}): Partial<AltYBootstrapApiConfig> {
  const api = configuredApi(options);
  const auth = configuredAuth(options);
  const baseUrl = cleanUrl(api.baseUrl) || '/api/alty';
  return {
    baseUrl,
    bootstrapUrl: cleanUrl(api.bootstrapUrl) || `${baseUrl}/bootstrap`,
    mintUrl: cleanUrl(api.mintUrl) || `${baseUrl}/mint`,
    authMeUrl: cleanUrl(api.authMeUrl) || `${baseUrl}/auth/me`,
    loginRequestUrl: cleanUrl(api.loginRequestUrl) || cleanUrl(auth.loginRequestUrl) || `${baseUrl}/auth/request-code`,
    loginVerifyUrl: cleanUrl(api.loginVerifyUrl) || cleanUrl(auth.loginVerifyUrl) || `${baseUrl}/auth/verify-code`,
    logoutUrl: cleanUrl(api.logoutUrl) || cleanUrl(auth.logoutUrl) || `${baseUrl}/auth/logout`,
  };
}

function isConfiguredAltYUrl(url: string, options: NativeAuthOptions = {}): boolean {
  const api = authUrls(options);
  return urlMatches(url, api.baseUrl)
    || urlEquals(url, api.bootstrapUrl)
    || urlEquals(url, api.mintUrl)
    || urlEquals(url, api.authMeUrl)
    || urlEquals(url, api.loginRequestUrl)
    || urlEquals(url, api.loginVerifyUrl)
    || urlEquals(url, api.logoutUrl);
}

function isAuthPath(url: string, suffix: string): boolean {
  try {
    return new URL(url).pathname.endsWith(`/auth/${suffix}`);
  } catch {
    return false;
  }
}

function isLoginVerifyUrl(url: string, options: NativeAuthOptions = {}): boolean {
  return isAuthPath(url, 'verify-code') || urlEquals(url, authUrls(options).loginVerifyUrl);
}

function isLogoutUrl(url: string, options: NativeAuthOptions = {}): boolean {
  return isAuthPath(url, 'logout') || urlEquals(url, authUrls(options).logoutUrl);
}

function tauriInvoke(): AltYTauriInvoke | undefined {
  const invoke = window.__TAURI__?.core?.invoke;
  return typeof invoke === 'function' ? invoke : undefined;
}

function tauriNativeAuthBridge(): NativeAuthBridge | null {
  const invoke = tauriInvoke();
  if (!invoke) return null;

  return {
    async getAuthToken() {
      const token = await invoke('get_auth_token');
      return typeof token === 'string' && token ? token : null;
    },
    async setAuthToken(token: string) {
      if (token) await invoke('set_auth_token', { token });
      else await invoke('clear_auth_token');
    },
    async clearAuthToken() {
      await invoke('clear_auth_token');
    },
  };
}

function nativeAuthBridge(): NativeAuthBridge | null {
  const nativeAuth = window.altYNativeAuth;
  if (nativeAuth?.getAuthToken || nativeAuth?.setAuthToken || nativeAuth?.clearAuthToken) {
    return nativeAuth;
  }
  const mobileBridge = window.AltYMobileBridge;
  if (mobileBridge?.getAuthToken || mobileBridge?.setAuthToken || mobileBridge?.clearAuthToken) {
    return mobileBridge;
  }
  return tauriNativeAuthBridge();
}

export async function getNativeAuthToken(): Promise<string | null> {
  try {
    const token = await nativeAuthBridge()?.getAuthToken?.();
    return typeof token === 'string' && token ? token : null;
  } catch {
    return null;
  }
}

async function setNativeAuthToken(token: string): Promise<void> {
  try {
    await nativeAuthBridge()?.setAuthToken?.(token);
  } catch {
    /* Native token storage is best-effort; cookie auth still applies. */
  }
}

async function clearNativeAuthToken(): Promise<void> {
  try {
    const bridge = nativeAuthBridge();
    if (bridge?.clearAuthToken) await bridge.clearAuthToken();
    else await bridge?.setAuthToken?.('');
  } catch {
    /* Native token storage is best-effort; cookie auth still applies. */
  }
}

async function persistNativeTokenFromResponse(response: Response): Promise<void> {
  try {
    const data = await response.clone().json() as { nativeAuthToken?: unknown; sessionToken?: unknown };
    const token = typeof data.nativeAuthToken === 'string' && data.nativeAuthToken
      ? data.nativeAuthToken
      : typeof data.sessionToken === 'string' && data.sessionToken
        ? data.sessionToken
        : '';
    if (token) await setNativeAuthToken(token);
  } catch {
    /* Non-JSON auth responses are allowed; the transport check covers shape. */
  }
}

export async function fetchWithAltYNativeAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: NativeAuthOptions = {},
): Promise<Response> {
  const bridge = nativeAuthBridge();
  if (!bridge) return fetch(input, init);

  const url = inputUrl(input);
  const shouldHandle = !!url && isConfiguredAltYUrl(url, options);
  if (!shouldHandle) return fetch(input, init);

  const headers = new Headers(init.headers || inputHeaders(input));
  const token = await getNativeAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('X-Alty-Native-Auth')) {
    headers.set('X-Alty-Native-Auth', '1');
  }

  const response = await fetch(input, {
    ...init,
    credentials: init.credentials || inputCredentials(input) || 'include',
    headers,
  });

  if (response.ok && isLoginVerifyUrl(url, options)) await persistNativeTokenFromResponse(response);
  if (response.ok && isLogoutUrl(url, options)) await clearNativeAuthToken();
  return response;
}
